import React, { MouseEvent, useEffect, useLayoutEffect, useRef } from 'react';
import {
    AiOutlineClose,
    AiOutlineEdit,
    AiOutlineHistory,
    AiOutlineInfoCircle,
} from 'react-icons/ai';
import { BsArrowLeftShort } from 'react-icons/bs';
import {
    Box,
    Button,
    ButtonGroup,
    ButtonProps,
    Divider,
    Grid,
    GridItem,
    Heading,
    HStack,
    Icon,
    IconButton,
    Image,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverHeader,
    PopoverTrigger,
    Progress,
    ProgressProps,
    SimpleGrid,
    StackDivider,
    StackProps,
    Table,
    TableContainer,
    Tag,
    Tbody,
    Td,
    Text,
    TextProps,
    Th,
    Thead,
    Tooltip,
    Tr,
    useDisclosure,
    VStack,
    Wrap,
    useToken,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Graphics } from '../../../../../../config/svg';
import { ReportingViewBaseConfig } from '../../base';
import {
    ReportSectionItemContainerProps,
    ReportSectionItemViewProps,
    SourceListViewProps,
} from '../../view';
// import { TableContentProps } from '../../view/content/item';
import { BiCommentDetail } from 'react-icons/bi';
import { FiMaximize, FiShare } from 'react-icons/fi';
import { assertNever } from '@varos/util-typescript';
import { ReportChapterViewProps } from './reportChapterProps';
import { calculatePixelOffset } from './reportChapterHelper';
import { Block } from '@varos/research-sdk-api';

export function createReportChapterView(
    config: ReportingViewBaseConfig & {
        Container: {
            Section: React.FC<ReportSectionItemContainerProps>;
        };
    }
): React.FC<ReportChapterViewProps> {
    const {
        UI: {
            Application: { Link, Anchor },
        },
        Layout: {
            Centered: {
                Component: {
                    Root: LayoutRoot,
                    Header: LayoutHeader,
                    Content: LayoutContent,
                    Body: LayoutBody,
                },
            },
        },
        Container: { Section: SectionContainer },
    } = config;

    const colors = {
        breadcrumb: 'blue.300',
        breadcrumbHover: 'blue.400',
        caption: 'whiteAlpha.500',
        chevron: 'whiteAlpha.500',
    } satisfies Record<string, TextProps['color']>;

    const colorSchemes = {
        tag: 'blue',
    } satisfies Record<string, ButtonProps['colorScheme']>;

    const SourceRankTag: React.FC<
        React.PropsWithChildren &
            StackProps & { colorScheme?: ButtonProps['colorScheme'] }
    > = (props) => {
        const { colorScheme = 'green', ...rest } = props;

        // Map colorScheme to actual color
        const bgColor =
            colorScheme === 'red'
                ? 'red.200'
                : colorScheme === 'orange'
                  ? 'orange.200'
                  : 'green.200';

        return (
            <HStack
                flexShrink={1}
                textTransform="uppercase"
                letterSpacing="wider"
                fontWeight="bold"
                fontSize="sm"
                userSelect="none"
                bg={bgColor}
                color="black"
                borderRadius="full"
                minH={0}
                lineHeight={1}
                py={0}
                px={3}
                textAlign="center"
                {...rest}
            >
                <Text>{props.children}</Text>
            </HStack>
        );
    };

    // Helper function to map colorScheme to Chakra color
    const getColorFromScheme = (
        colorScheme: ButtonProps['colorScheme'],
        amount: number = 200
    ): string => {
        if (colorScheme === 'red') return `red.${amount}`;
        if (colorScheme === 'orange') return `orange.${amount}`;
        if (colorScheme === 'green') return `green.${amount}`;
        return `green.${amount}`; // default
    };

    const RankingPopover: React.FC<{
        score: Exclude<ReportSectionItemViewProps['score'], undefined | null>;
    }> = ({ score }) => {
        return (
            <VStack
                align="start"
                w="full"
                divider={<StackDivider borderWidth={0.5} borderColor="whiteAlpha.200" />}
                spacing={0}
            >
                <HStack px={4} py={2} justify="space-between" w="full">
                    <Text fontWeight="semibold" color="whiteAlpha.900">
                        SourceRank
                    </Text>
                    <HStack
                        fontSize="sm"
                        letterSpacing="wider"
                        color="whiteAlpha.600"
                        spacing={1}
                        fontWeight="medium"
                    >
                        <Text
                            color={getColorFromScheme(score.total.colorScheme)}
                            fontWeight="bold"
                        >
                            {score.total.numeric.toFixed(1)}
                        </Text>
                        <Text>/</Text>
                        <Text>10</Text>
                    </HStack>
                </HStack>
                <VStack
                    align="start"
                    w="full"
                    divider={
                        <StackDivider borderWidth={0.5} borderColor="whiteAlpha.200" />
                    }
                    spacing={0}
                    fontSize="sm"
                >
                    <VStack
                        align="start"
                        w="full"
                        spacing={4}
                        p={4}
                        pb={8}
                        maxH="20rem"
                        overflowY="auto"
                        sx={{
                            ...scrollbarSx,
                            '&::-webkit-scrollbar': {
                                width: '8px',
                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                            },
                        }}
                        divider={
                            <StackDivider borderWidth={1} borderColor="whiteAlpha.100" />
                        }
                    >
                        <Text color="whiteAlpha.800" fontSize="sm" fontWeight="medium">
                            <Box
                                w={2}
                                h={2}
                                borderRadius="full"
                                bg={getColorFromScheme(score.total.colorScheme, 300)}
                                display="inline-block"
                                mr={2}
                                pos="relative"
                                bottom="1px"
                            />
                            <Box as="span">{score.total.text}</Box>
                        </Text>
                        <VStack w="full" spacing={6}>
                            {score?.detail.map((detailItem) => (
                                <VStack
                                    key={detailItem.id}
                                    align="start"
                                    w="full"
                                    spacing={2}
                                >
                                    <VStack align="start" w="full" spacing={0}>
                                        <Text
                                            color="whiteAlpha.500"
                                            fontWeight="bold"
                                            textTransform="uppercase"
                                            fontSize="xs"
                                            letterSpacing="wide"
                                        >
                                            {detailItem.label}
                                        </Text>
                                        <Text
                                            color="whiteAlpha.800"
                                            fontWeight="semibold"
                                            noOfLines={1}
                                        >
                                            {detailItem.value.label}
                                        </Text>
                                    </VStack>
                                    <Progress
                                        colorScheme={detailItem.progress.colorScheme}
                                        size="sm"
                                        value={detailItem.progress.value}
                                        w="full"
                                    />
                                </VStack>
                            ))}
                        </VStack>
                    </VStack>
                    <HStack w="full" justify="start" p={4}>
                        <Text
                            fontWeight="medium"
                            color="whiteAlpha.400"
                            cursor="pointer"
                            _hover={{ textDecor: 'underline' }}
                        >
                            Learn more about SourceRank
                        </Text>
                    </HStack>
                </VStack>
            </VStack>
        );
    };

    // Helper function to get a valid colorScheme from a progress object
    const getColorSchemeFromProgress = (
        progress?: Pick<ProgressProps, 'colorScheme' | 'value'>
    ): ButtonProps['colorScheme'] => {
        if (!progress || !progress.colorScheme) return 'orange';

        const scheme = progress.colorScheme;
        if (scheme === 'red' || scheme === 'green' || scheme === 'orange') {
            return scheme;
        }
        return 'orange';
    };

    const SourceList: React.FC<
        Pick<SourceListViewProps, 'items'> & {
            score?: ReportSectionItemViewProps['score'];
        }
    > = (props) => {
        return (
            <VStack
                align="start"
                w="full"
                divider={<StackDivider borderWidth={0.5} borderColor="whiteAlpha.200" />}
                spacing={0}
            >
                <HStack px={4} py={2}>
                    <Text fontWeight="semibold" color="whiteAlpha.900">
                        Sources
                    </Text>
                </HStack>
                <VStack
                    align="start"
                    w="full"
                    divider={
                        <StackDivider borderWidth={0.5} borderColor="whiteAlpha.200" />
                    }
                    spacing={0}
                    fontSize="sm"
                    maxH="20rem"
                    overflowY="auto"
                    sx={{
                        ...scrollbarSx,
                        '&::-webkit-scrollbar': {
                            width: '8px',
                            backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        },
                    }}
                >
                    {props.items.map((item) => {
                        return (
                            <VStack
                                key={item.id}
                                align="start"
                                w="full"
                                p={4}
                                spacing={1}
                            >
                                <HStack
                                    w="full"
                                    justify="space-between"
                                    spacing={6}
                                    align="center"
                                >
                                    <HStack
                                        spacing={3}
                                        onClick={item.onOpen ?? undefined}
                                    >
                                        {item.image && (
                                            <Image
                                                src={item.image.src}
                                                alt={item.label}
                                                w={3}
                                                h={3}
                                            />
                                        )}
                                        <Text
                                            noOfLines={1}
                                            fontWeight="semibold"
                                            color="whiteAlpha.800"
                                            cursor="pointer"
                                            _hover={{ textDecoration: 'underline' }}
                                        >
                                            {item.label}
                                        </Text>
                                    </HStack>
                                    <SourceRankTag
                                        h={6}
                                        colorScheme={item.score.total.colorScheme}
                                    >
                                        {item.score.total.numeric.toFixed(1)}
                                    </SourceRankTag>
                                </HStack>
                                <Text color="whiteAlpha.600" noOfLines={2}>
                                    {item.description}
                                </Text>
                                {/* {item.anchor && (
                                    <Anchor
                                        {...item.anchor}
                                        color="blue.300"
                                        target="_blank"
                                        fontWeight="medium"
                                    >
                                        <Text noOfLines={1}>View source</Text>
                                    </Anchor>
                                )} */}
                            </VStack>
                        );
                    })}
                </VStack>
            </VStack>
        );
    };

    // const TextContent: React.FC<TextContentProps> = (props) => {
    //     return <Text color="inherit">{props.text}</Text>;
    // };

    const scrollbarSx = {
        '&::-webkit-scrollbar': {
            height: '8px',
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
        },
        '&::-webkit-scrollbar-thumb': {
            background: 'whiteAlpha.300',
            borderRadius: '8px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: 'whiteAlpha.400',
        },
    };

    // Use Props types for Table components
    // type TableColumn = TableContentProps['columns'][number];
    // type TableRow = TableContentProps['rows'][number];

    // interface TableViewProps extends TableContentProps {
    //     scrollable?: boolean;
    // }

    interface TableViewProps {
        scrollable?: boolean;
    }

    const TableView: React.FC<TableViewProps> = ({ scrollable = true, ...props }) => {
        return <>Table block not implemented</>;
        // return (
        //     <TableContainer
        //         w="full"
        //         {...(scrollable && { overflowX: 'auto', maxW: '100%', sx: scrollbarSx })}
        //         {...(!scrollable && { h: 'full' })}
        //     >
        //         <Table variant="simple" colorScheme="whiteAlpha">
        //             <Thead>
        //                 <Tr>
        //                     {props.columns.map((column: TableColumn, index: number) => (
        //                         <Th
        //                             key={column.key}
        //                             whiteSpace="nowrap"
        //                             borderColor="whiteAlpha.200"
        //                             borderBottomWidth={2}
        //                             py={3}
        //                             fontSize="xs"
        //                             fontWeight="bold"
        //                             color="whiteAlpha.500"
        //                             textTransform="uppercase"
        //                             letterSpacing="wider"
        //                             {...(index === 0
        //                                 ? {
        //                                       px: 0,
        //                                   }
        //                                 : {})}
        //                             {...(index > 0
        //                                 ? {
        //                                       px: 4,
        //                                   }
        //                                 : {})}
        //                         >
        //                             {column.title}
        //                         </Th>
        //                     ))}
        //                 </Tr>
        //             </Thead>
        //             <Tbody>
        //                 {props.rows.map((row: TableRow) => (
        //                     <Tr key={row.id}>
        //                         {props.columns.map(
        //                             (column: TableColumn, index: number) => (
        //                                 <Td
        //                                     key={column.key}
        //                                     whiteSpace="nowrap"
        //                                     py={4}
        //                                     borderColor="whiteAlpha.200"
        //                                     {...(index === 0
        //                                         ? {
        //                                               px: 0,
        //                                               fontWeight: 'semibold',
        //                                               color: 'whiteAlpha.900',
        //                                           }
        //                                         : {})}
        //                                     {...(index > 0
        //                                         ? {
        //                                               color: 'whiteAlpha.700',
        //                                               px: 4,
        //                                           }
        //                                         : {})}
        //                                 >
        //                                     <HStack>
        //                                         <Text>
        //                                             {row.data[column.key] !== null &&
        //                                             row.data[column.key] !== undefined
        //                                                 ? (row.data[
        //                                                       column.key
        //                                                   ]?.toString() ?? 'N/A')
        //                                                 : '-'}
        //                                         </Text>
        //                                     </HStack>
        //                                 </Td>
        //                             )
        //                         )}
        //                     </Tr>
        //                 ))}
        //             </Tbody>
        //         </Table>
        //     </TableContainer>
        // );
    };

    // const TableContent: React.FC<TableContentProps> = (props) => {
    //     const { isOpen, onOpen, onClose } = useDisclosure();

    //     return (
    //         <VStack align="start" w="full" spacing={2} color="inherit">
    //             <HStack py={2} w="full" justify="space-between">
    //                 <Text fontWeight="semibold" color="whiteAlpha.800">
    //                     {props.title}
    //                 </Text>
    //                 <Tooltip
    //                     placement="top"
    //                     openDelay={300}
    //                     py={2}
    //                     px={3}
    //                     bg="gray.50"
    //                     color="gray.900"
    //                     shouldWrapChildren={true}
    //                     hasArrow={true}
    //                     label={`Open table in fullscreen view`}
    //                 >
    //                     <IconButton
    //                         aria-label="Open table in fullscreen view"
    //                         icon={<Icon as={FiMaximize} />}
    //                         size="sm"
    //                         variant="ghost"
    //                         color="whiteAlpha.700"
    //                         borderWidth={1}
    //                         borderColor="whiteAlpha.200"
    //                         bg="none"
    //                         _hover={{ bg: 'whiteAlpha.50' }}
    //                         onClick={onOpen}
    //                     />
    //                 </Tooltip>
    //             </HStack>
    //             <TableView {...props} />
    //             <Modal
    //                 isOpen={isOpen}
    //                 onClose={onClose}
    //                 size="full"
    //                 returnFocusOnClose={false}
    //             >
    //                 <ModalOverlay bg="blackAlpha.800" />
    //                 <ModalContent
    //                     bg="black"
    //                     color="whiteAlpha.900"
    //                     display="flex"
    //                     flexDirection="column"
    //                     h="full"
    //                     minH="0"
    //                 >
    //                     <ModalBody bg="whiteAlpha.50" p={0} flex="1">
    //                         <Grid
    //                             w="full"
    //                             h="full"
    //                             gap={4}
    //                             templateRows="min-content 1fr min-content"
    //                         >
    //                             <GridItem
    //                                 p={6}
    //                                 borderBottomWidth={2}
    //                                 borderColor="whiteAlpha.200"
    //                             >
    //                                 <HStack w="full" justify="space-between">
    //                                     <Heading
    //                                         fontSize="lg"
    //                                         fontWeight="semibold"
    //                                         color="whiteAlpha.900"
    //                                     >
    //                                         {props.title}
    //                                     </Heading>
    //                                     <ModalCloseButton
    //                                         as={IconButton}
    //                                         icon={<Icon as={AiOutlineClose} />}
    //                                         variant="ghost"
    //                                         color="whiteAlpha.700"
    //                                         borderWidth={1}
    //                                         top={4}
    //                                         right={4}
    //                                         borderColor="whiteAlpha.200"
    //                                         borderRadius="full"
    //                                         size="lg"
    //                                         bg="none"
    //                                         _hover={{ bg: 'whiteAlpha.50' }}
    //                                     />
    //                                 </HStack>
    //                             </GridItem>
    //                             <GridItem
    //                                 px={6}
    //                                 py={0}
    //                                 overflowX="scroll"
    //                                 sx={scrollbarSx}
    //                             >
    //                                 <TableView {...props} scrollable={false} />
    //                             </GridItem>
    //                             <GridItem
    //                                 p={4}
    //                                 borderTopWidth={2}
    //                                 borderColor="whiteAlpha.200"
    //                             >
    //                                 <Image
    //                                     src={Graphics.Brand.WhiteText}
    //                                     alt="Varos"
    //                                     h={8}
    //                                     w="auto"
    //                                 />
    //                             </GridItem>
    //                         </Grid>
    //                     </ModalBody>
    //                 </ModalContent>
    //             </Modal>
    //         </VStack>
    //     );
    // };

    // // Renders ONLY Text or Table block
    // const ContentBlockRenderer: React.FC<{
    //     block: TextContentProps | TableContentProps;
    //     blockKey: string;
    // }> = ({ block, blockKey }) => {
    //     if (block.kind === 'text') {
    //         return <TextContent key={blockKey} {...block} />;
    //     } else if (block.kind === 'table') {
    //         return <TableContent key={blockKey} {...block} />;
    //     }
    //     // Should not be reached if called correctly
    //     return assertNever(block);
    // };

    // Renders a single Text or Table block
    const BlockItem: React.FC<{
        item: Block;
    }> = (props) => {
        if (props.item.data.kind === 'text') {
            return props.item.data.items.flatMap((item, index) => {
                if (item.kind === 'text') {
                    return (
                        <Text key={`${props.item.id}-${index}`} color="inherit">
                            {item.text}
                        </Text>
                    );
                }
                console.warn('unexpected text span', item);
                return [];
            });
        }
        if (props.item.data.kind === 'heading') {
            const { level } = props.item.data;
            return props.item.data.items.flatMap((item, index) => {
                if (item.kind === 'text') {
                    return (
                        <Heading
                            key={`${props.item.id}-${index}`}
                            as={`h${level as 1 | 2 | 3 | 4 | 5 | 6}`}
                            fontSize="medium"
                            fontWeight="semibold"
                            color="whiteAlpha.800"
                        >
                            {item.text}
                        </Heading>
                    );
                }
                console.warn('unexpected heading span', item);
                return [];
            });
        }
        if (props.item.data.kind === 'divider') {
            return <Divider borderColor="whiteAlpha.200" />;
        }
        if (props.item.data.kind === 'footnote') {
            console.warn('footnote not supported', props.item);
            return <></>;
        }
        throw new Error(`unexpected block kind: ${props.item.data.kind}`);
        // if (item.kind === 'subsection') {
        //     return (
        //         <VStack key={`${itemKey}-subsection`} align="start" spacing={3} w="full">
        //             {item.title && (
        //                 <Heading
        //                     as="h4"
        //                     fontSize="md"
        //                     fontWeight="semibold"
        //                     color="whiteAlpha.800"
        //                 >
        //                     {item.title}
        //                 </Heading>
        //             )}
        //             {item.content.map((block, blockIndex) => (
        //                 <ContentBlockRenderer
        //                     key={`${itemKey}-subsection-block-${blockIndex}`}
        //                     block={block}
        //                     blockKey={`${itemKey}-subsection-block-${blockIndex}`}
        //                 />
        //             ))}
        //         </VStack>
        //     );
        // }

        // if (item.kind === 'standalone') {
        //     const contentBlock = item.content;
        //     if (contentBlock.kind === 'divider') {
        //         return (
        //             <Divider
        //                 key={`${itemKey}-divider`}
        //                 borderColor="whiteAlpha.200"
        //                 borderWidth={1}
        //                 maxW="12rem"
        //             />
        //         );
        //     } else if (contentBlock.kind === 'text' || contentBlock.kind === 'table') {
        //         return (
        //             <ContentBlockRenderer
        //                 key={`${itemKey}-standalone`}
        //                 block={contentBlock}
        //                 blockKey={`${itemKey}-standalone`}
        //             />
        //         );
        //     }
        //     return assertNever(contentBlock);
        // }

        // return assertNever(item);
    };

    const SectionItem: React.FC<ReportSectionItemViewProps> = (props) => {
        const { blocks, ...item } = props;
        return (
            <VStack
                id={item.id}
                align="start"
                w="full"
                spacing={4}
                {...(!props.isLast
                    ? {
                          pb: 8,
                          borderBottomWidth: 3,
                          borderStyle: 'dashed',
                          borderColor: 'whiteAlpha.200',
                      }
                    : {})}
            >
                <HStack w="full" justify="space-between" align="start">
                    <VStack align="start" spacing={1}>
                        <Text
                            fontWeight="bold"
                            fontSize="sm"
                            textTransform="uppercase"
                            letterSpacing="wider"
                            color={colors.caption}
                        >
                            {item.title}
                        </Text>
                        {item.caption && (
                            <Heading
                                fontWeight="medium"
                                fontSize="xl"
                                color="whiteAlpha.800"
                            >
                                {item.caption}
                            </Heading>
                        )}
                    </VStack>
                    {item.score && (
                        <Popover placement="top-end" trigger="hover" openDelay={300}>
                            <PopoverTrigger>
                                <Box>
                                    <SourceRankTag
                                        h={7}
                                        colorScheme={item.score.total.colorScheme}
                                    >
                                        {item.score.total.numeric.toFixed(1)}
                                    </SourceRankTag>
                                </Box>
                            </PopoverTrigger>
                            <PopoverContent
                                overflow="hidden"
                                bg="black"
                                _focusVisible={{
                                    outline: 'none',
                                }}
                            >
                                <PopoverBody p={0} bg="whiteAlpha.200">
                                    <RankingPopover score={item.score} />
                                </PopoverBody>
                            </PopoverContent>
                        </Popover>
                    )}
                </HStack>
                <VStack align="start" spacing={4} color="whiteAlpha.700" w="full">
                    {blocks.map((block, blockIndex) => (
                        <BlockItem
                            key={`${item.id}-content-${blockIndex}`}
                            item={block}
                        />
                    ))}
                </VStack>
                <HStack w="full" justify="space-between" spacing={4}>
                    <HStack spacing={3} flexBasis={'32rem'}>
                        {item.action && (
                            <Button
                                {...item.action.button}
                                flexShrink={0}
                                borderRadius="full"
                                borderWidth={2}
                                borderColor="whiteAlpha.200"
                                bg="none"
                                color="whiteAlpha.800"
                                _hover={{ bg: 'whiteAlpha.50' }}
                            >
                                {item.action.label}
                            </Button>
                        )}
                        <Wrap
                            shouldWrapChildren={true}
                            w="full"
                            spacing={2}
                            userSelect="none"
                        >
                            {item.keywords.map((keyword) => (
                                <Tag
                                    key={keyword.id}
                                    colorScheme={colorSchemes.tag}
                                    size="lg"
                                    borderRadius="sm"
                                    fontWeight="medium"
                                >
                                    {keyword.label}
                                </Tag>
                            ))}
                        </Wrap>
                    </HStack>
                    {item.source && (
                        <Popover placement="top-end" trigger="hover" openDelay={300}>
                            <PopoverTrigger>
                                <HStack
                                    spacing={2}
                                    fontWeight="medium"
                                    justify="end"
                                    w="full"
                                    fontStyle="italic"
                                    fontSize="sm"
                                    userSelect="none"
                                    color="whiteAlpha.400"
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                    // {...(item.source.onOpen
                                    //     ? {
                                    //           onOpen(
                                    //               event: React.MouseEvent<HTMLDivElement>
                                    //           ) {
                                    //               item.source?.onOpen?.(event);
                                    //               event.stopPropagation();
                                    //               event.preventDefault();
                                    //           },
                                    //           cursor: 'pointer',
                                    //           _hover: {
                                    //               textDecoration: 'underline',
                                    //           },
                                    //       }
                                    //     : {})}
                                >
                                    <Text noOfLines={1}>
                                        {item.source.image && (
                                            <Image
                                                display="inline"
                                                src={item.source.image.src}
                                                alt={item.source.label}
                                                w={3}
                                                h={3}
                                            />
                                        )}
                                        <Box as="span">{item.source.label}</Box>
                                    </Text>
                                    <Box as="span">
                                        <Icon as={AiOutlineInfoCircle} />
                                    </Box>
                                </HStack>
                            </PopoverTrigger>
                            <PopoverContent
                                overflow="hidden"
                                bg="black"
                                _focusVisible={{ outline: 'none' }}
                                w="full"
                                maxW="24rem"
                            >
                                <PopoverBody p={0} bg="whiteAlpha.200">
                                    <SourceList {...item.source} score={item.score} />
                                </PopoverBody>
                            </PopoverContent>
                        </Popover>
                    )}
                </HStack>
            </VStack>
        );
    };

    // Helper function to determine color scheme based on score
    const getColorScheme = (score: number): 'red' | 'orange' | 'green' => {
        if (score < 4) return 'red';
        if (score > 7) return 'green';
        return 'orange';
    };

    return (props) => {
        const [gutterValueRem] = useToken('space', ['8']);
        const offset = calculatePixelOffset(gutterValueRem);
        return (
            <LayoutRoot>
                <LayoutContent>
                    <LayoutBody>
                        <VStack align="start" w="full" spacing={12}>
                            <VStack w="full" align="start" spacing={8}>
                                <VStack w="full" align="start" spacing={8}>
                                    <HStack w="full" justify="space-between">
                                        <HStack
                                            spacing={2}
                                            fontSize="md"
                                            fontWeight="medium"
                                            divider={
                                                <Icon
                                                    border="none"
                                                    color="whiteAlpha.400"
                                                    fontSize="xl"
                                                    as={ChevronRightIcon}
                                                />
                                            }
                                        >
                                            <Link {...props.back}>
                                                <HStack spacing={4}>
                                                    <IconButton
                                                        aria-label="back"
                                                        borderRadius="full"
                                                        borderWidth={1}
                                                        borderColor="whiteAlpha.300"
                                                        bg="none"
                                                        _hover={{
                                                            bg: 'whiteAlpha.50',
                                                        }}
                                                        icon={
                                                            <Icon
                                                                fontSize="2xl"
                                                                color="whiteAlpha.800"
                                                                as={BsArrowLeftShort}
                                                            />
                                                        }
                                                    />
                                                    <Text
                                                        color={colors.breadcrumb}
                                                        _hover={{
                                                            color: colors.breadcrumbHover,
                                                        }}
                                                    >
                                                        {props.parent.title}
                                                    </Text>
                                                </HStack>
                                            </Link>
                                        </HStack>
                                        <HStack spacing={3}>
                                            <HStack spacing={1}>
                                                <Tooltip
                                                    placement="bottom"
                                                    openDelay={300}
                                                    px={2}
                                                    py={1}
                                                    bg="gray.50"
                                                    color="gray.900"
                                                    shouldWrapChildren={true}
                                                    label={`Last edited 3 hours ago by ${props.author.label}`}
                                                >
                                                    <IconButton
                                                        aria-label="back"
                                                        bg="none"
                                                        _hover={{
                                                            bg: 'whiteAlpha.50',
                                                        }}
                                                        icon={
                                                            <Icon
                                                                fontSize="lg"
                                                                color="whiteAlpha.800"
                                                                as={AiOutlineHistory}
                                                            />
                                                        }
                                                    />
                                                </Tooltip>
                                                <Tooltip
                                                    placement="bottom"
                                                    openDelay={300}
                                                    px={2}
                                                    py={1}
                                                    bg="gray.50"
                                                    color="gray.900"
                                                    shouldWrapChildren={true}
                                                    label="Show all comments"
                                                >
                                                    <IconButton
                                                        aria-label="back"
                                                        bg="none"
                                                        _hover={{
                                                            bg: 'whiteAlpha.50',
                                                        }}
                                                        icon={
                                                            <Icon
                                                                fontSize="lg"
                                                                color="whiteAlpha.800"
                                                                as={BiCommentDetail}
                                                            />
                                                        }
                                                    />
                                                </Tooltip>
                                            </HStack>
                                            <ButtonGroup
                                                isAttached={true}
                                                borderWidth={1}
                                                borderColor="whiteAlpha.300"
                                                variant="outline"
                                                borderRadius="full"
                                                overflow="hidden"
                                            >
                                                <Button
                                                    color="whiteAlpha.800"
                                                    bg="none"
                                                    _hover={{
                                                        bg: 'whiteAlpha.50',
                                                    }}
                                                    borderRightWidth={1}
                                                    borderRightColor="whiteAlpha.300"
                                                >
                                                    Share
                                                </Button>
                                                <IconButton
                                                    aria-label="more"
                                                    bg="none"
                                                    _hover={{
                                                        bg: 'whiteAlpha.50',
                                                    }}
                                                    icon={
                                                        <Icon
                                                            fontSize="lg"
                                                            as={ChevronDownIcon}
                                                        />
                                                    }
                                                />
                                            </ButtonGroup>
                                        </HStack>
                                    </HStack>
                                    <VStack align="start" w="full">
                                        <VStack align="start" spacing={1}>
                                            <Text
                                                textTransform="uppercase"
                                                fontWeight="bold"
                                                fontSize="sm"
                                                letterSpacing="wide"
                                                color={colors.caption}
                                            >
                                                {props.title}
                                            </Text>
                                            {props.caption && (
                                                <Heading
                                                    color="whiteAlpha.900"
                                                    fontSize="2xl"
                                                    fontWeight="semibold"
                                                >
                                                    {props.caption}
                                                </Heading>
                                            )}
                                        </VStack>
                                        <HStack
                                            flexShrink={0}
                                            spacing={1}
                                            color="whiteAlpha.500"
                                            userSelect="none"
                                        >
                                            <Text>Last edited</Text>
                                            <Tooltip
                                                placement="top"
                                                openDelay={300}
                                                py={2}
                                                px={3}
                                                bg="gray.50"
                                                color="gray.900"
                                                shouldWrapChildren={true}
                                                hasArrow={true}
                                                label={props.edited.timestamp.absolute}
                                            >
                                                <Text
                                                    fontWeight="semibold"
                                                    _hover={{ textDecor: 'underline' }}
                                                >
                                                    {props.edited.timestamp.relative}
                                                </Text>
                                            </Tooltip>
                                            <Text>by</Text>
                                            <Text
                                                fontWeight="semibold"
                                                _hover={{ textDecor: 'underline' }}
                                            >
                                                {props.author.label}
                                            </Text>
                                        </HStack>
                                    </VStack>
                                </VStack>
                                <VStack
                                    align="start"
                                    w="full"
                                    spacing={12}
                                    {...(props.parent.summary || props.hightlights
                                        ? {}
                                        : { display: 'none' })}
                                >
                                    {props.parent.summary && (
                                        <SimpleGrid
                                            columns={2}
                                            gap={6}
                                            w="full"
                                            templateColumns="min-content 1fr"
                                        >
                                            <Box maxH={16} py={1} h="full">
                                                <Box
                                                    bg="whiteAlpha.200"
                                                    w={1.5}
                                                    h="full"
                                                    borderRadius="full"
                                                />
                                            </Box>
                                            <Text fontSize="lg" color="whiteAlpha.700">
                                                {props.parent.summary}
                                            </Text>
                                        </SimpleGrid>
                                    )}
                                    {props.hightlights && (
                                        <Grid
                                            templateColumns={{
                                                base: 'repeat(1, minmax(0, 1fr))',
                                                md: 'repeat(2, minmax(0, 1fr))',
                                            }}
                                            gap={4}
                                            w="full"
                                            onClick={(event) =>
                                                props.onHighlightClick(event, offset)
                                            }
                                        >
                                            {props.hightlights.map((item) => (
                                                <GridItem key={item.id}>
                                                    <a
                                                        href={`#${item.id}`}
                                                        style={{ textDecoration: 'none' }}
                                                    >
                                                        <HStack
                                                            spacing={8}
                                                            justify="space-between"
                                                            w="full"
                                                            h="full"
                                                            py={4}
                                                            px={5}
                                                            cursor="pointer"
                                                            borderRadius="sm"
                                                            color="whiteAlpha.600"
                                                            bg="whiteAlpha.50"
                                                            _hover={{
                                                                bg: 'whiteAlpha.100',
                                                            }}
                                                            maxW="full"
                                                            fontWeight="medium"
                                                        >
                                                            <Text noOfLines={1}>
                                                                {item.label}
                                                            </Text>
                                                            <Icon
                                                                color={colors.chevron}
                                                                fontSize="2xl"
                                                                as={ChevronRightIcon}
                                                            />
                                                        </HStack>
                                                    </a>
                                                </GridItem>
                                            ))}
                                        </Grid>
                                    )}
                                </VStack>
                            </VStack>
                            <VStack align="start" w="full" spacing={8}>
                                {props.sections.map((itemProps) => (
                                    <SectionContainer
                                        key={itemProps.item.section.id}
                                        as={SectionItem}
                                        {...itemProps}
                                    />
                                ))}
                            </VStack>
                        </VStack>
                    </LayoutBody>
                </LayoutContent>
            </LayoutRoot>
        );
    };
}
